import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type GetUserEducationViewCountsResponse,
  type UpdateUserEducationViewCountsRequest,
} from './aliases';
import {
  fetchUserEducationViewCounts,
  updateUserEducationViewCounts,
} from './api';

import { RQ_USER_EDUCATION_COUNTS_KEY } from '@/modules/ReactQuery/cacheKeys';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props<T> = {
  isEnabled: boolean;
  educationComponentKey: keyof GetUserEducationViewCountsResponse;
  incrementTrigger?: T;
  limit?: number;
};

/**
 * Hook for components that track a view count in the back end to determine
 * whether or not to render. Updates optimistically when dismissed.
 *
 * @param isEnabled - set to false to force the component to not show
 * @param educationComponentKey - string identifier for the component
 * @param incrementTrigger - dependency for the increment use effect
 * @param limit - number of views before we stop showing the component
 */
export function useEducationViewTracker<T>({
  isEnabled,
  educationComponentKey,
  incrementTrigger,
  limit = 10,
}: Props<T>) {
  const [isAuthenticated] = useCurrentUser();
  const [shouldDisplayComponent, setShouldDisplayComponent] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: [RQ_USER_EDUCATION_COUNTS_KEY],
    queryFn: () => {
      if (!isAuthenticated) {
        return null;
      }
      return fetchUserEducationViewCounts();
    },
  });

  const incrementViewCountsMutation = useMutation({
    mutationFn: (counts: UpdateUserEducationViewCountsRequest) =>
      updateUserEducationViewCounts(counts),
    retry: 3,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RQ_USER_EDUCATION_COUNTS_KEY],
      });
    },
  });

  const userEducationViewCounts = data?.data;

  useEffect(() => {
    if (isEnabled && incrementTrigger && isAuthenticated && !isLoading) {
      const viewCount = userEducationViewCounts?.[educationComponentKey] || 0;

      if (viewCount <= limit) {
        setShouldDisplayComponent(true);
        incrementViewCountsMutation.mutate({
          [educationComponentKey]: 1,
        });
      }
    } else {
      setShouldDisplayComponent(false);
    }
  }, [incrementTrigger, isEnabled, isLoading]);

  function dismiss() {
    const viewCount =
      (userEducationViewCounts &&
        userEducationViewCounts[educationComponentKey]) ||
      0;
    setShouldDisplayComponent(false);
    incrementViewCountsMutation.mutate({
      [educationComponentKey]: limit - viewCount + 1,
    });
  }

  return {
    shouldDisplayComponent,
    dismiss,
  };
}
